import { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";
import { LogoFull } from "../components/Logo";
import { COMPANY } from "../data/company";
import { openCookiePreferences } from "../lib/consent";
import { useSeo, SEO } from "../lib/seo";
import { PrivacyPolicyBody, PRIVACY_UPDATED } from "../data/privacyPolicy";

const LegalData = () => (
    <dl className="grid gap-x-8 gap-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm sm:grid-cols-2" data-testid="legal-company-data">
        {[
            ["Titolare del trattamento", COMPANY.name],
            ["Sede legale", COMPANY.legalAddress],
            ["Sede operativa", COMPANY.operationalAddress],
            ["Partita IVA", COMPANY.vat],
            ["Codice Fiscale", COMPANY.fiscalCode],
            ["Numero REA", COMPANY.rea],
            ["Capitale sociale", COMPANY.shareCapital],
            ["PEC", COMPANY.pec],
            ["Email", COMPANY.email],
            ["Telefono", COMPANY.phone],
        ].map(([k, v]) => (
            <div key={k}>
                <dt className="text-[10px] font-bold uppercase tracking-wider text-[#4F5D70]">{k}</dt>
                <dd className="mt-0.5 font-semibold text-[#0B1F3A]">{v}</dd>
            </div>
        ))}
    </dl>
);

const PAGES = {
    "privacy-policy": {
        title: "Privacy Policy – FTM Group S.r.l.",
        subtitle: "Informativa sul trattamento dei dati personali ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR).",
        updated: PRIVACY_UPDATED,
        body: <PrivacyPolicyBody />,
    },
    "cookie-policy": {
        title: "Cookie Policy",
        subtitle: "Informazioni sull'uso dei cookie e delle tecnologie simili su questo sito, ai sensi del provvedimento del Garante del 10 giugno 2021.",
        updated: PRIVACY_UPDATED,
        body: (
            <>
                <h2>1. Cosa sono i cookie</h2>
                <p>I cookie sono piccoli file di testo memorizzati dal browser. Questo sito utilizza solo cookie tecnici di prima parte e, previo consenso, contenuti di terze parti che possono impostare propri cookie.</p>
                <h2>2. Cookie tecnici necessari (sempre attivi)</h2>
                <table>
                    <thead><tr><th>Nome</th><th>Finalità</th><th>Durata</th></tr></thead>
                    <tbody>
                        <tr><td>ftm_cookie_consent_v1</td><td>Memorizza le tue preferenze sui cookie</td><td>12 mesi (localStorage)</td></tr>
                        <tr><td>ftm_admin_token</td><td>Sessione dell'area riservata (solo personale FTM)</td><td>8 ore</td></tr>
                    </tbody>
                </table>
                <h2>3. Contenuti esterni (solo con consenso)</h2>
                <p>I video incorporati provengono da <strong>youtube-nocookie.com</strong> (Google Ireland Ltd.). Vengono caricati esclusivamente dopo il tuo consenso alla categoria "Contenuti esterni"; fino ad allora viene mostrato un riquadro sostitutivo. Google può trattare dati secondo la propria <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">informativa</a>.</p>
                <h2>4. Statistiche</h2>
                <p>Attualmente non è attivo alcun servizio di analisi statistica. Se in futuro verrà attivato, sarà utilizzato solo con il tuo consenso e questa pagina sarà aggiornata.</p>
                <h2>5. Come gestire le preferenze</h2>
                <p>Puoi modificare o revocare il consenso in qualsiasi momento dal pulsante qui sotto o dal link "Preferenze cookie" nel footer. Puoi inoltre eliminare i cookie dalle impostazioni del browser.</p>
                <p>
                    <button type="button" onClick={openCookiePreferences} data-testid="legal-open-cookie-preferences" className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#132b4f]">
                        <Cookie className="h-4 w-4" /> Apri preferenze cookie
                    </button>
                </p>
                <h2>6. Titolare</h2>
                <LegalData />
            </>
        ),
    },
};

export default function LegalPage() {
    const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
    const page = PAGES[slug];
    useSeo(SEO[slug] || SEO.home);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);
    if (!page) return <Navigate to="/" replace />;
    return (
        <div className="min-h-screen bg-white" data-testid={`legal-page-${slug}`}>
            <header className="border-b border-slate-200">
                <div className="mx-auto flex h-[76px] max-w-4xl items-center justify-between px-4 sm:px-8">
                    <Link to="/" aria-label="Home"><LogoFull /></Link>
                    <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#0B1F3A] hover:text-[#E30613]" data-testid="legal-back-home">
                        <ArrowLeft className="h-4 w-4" /> Torna al sito
                    </Link>
                </div>
            </header>
            <main className="mx-auto max-w-4xl px-4 py-14 sm:px-8">
                <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">Note legali</span>
                <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-5xl">{page.title}</h1>
                <p className="mt-4 text-base text-[#4F5D70]">{page.subtitle}</p>
                <p className="mt-2 text-xs text-[#94A3B8]" data-testid="legal-updated">Ultimo aggiornamento: {page.updated}</p>
                <article className="legal-prose mt-10">{page.body}</article>
                <div className="mt-12 flex flex-wrap gap-4 border-t border-slate-200 pt-8 text-sm font-semibold">
                    <Link to="/privacy-policy" className="text-[#0B1F3A] hover:text-[#E30613]">Privacy Policy</Link>
                    <Link to="/cookie-policy" className="text-[#0B1F3A] hover:text-[#E30613]">Cookie Policy</Link>
                    <button type="button" onClick={openCookiePreferences} className="text-[#0B1F3A] hover:text-[#E30613]">Preferenze cookie</button>
                </div>
            </main>
        </div>
    );
}
